import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useTracking } from "react-tracking";
import API from "src/http-common";

import { TimeoutWarningModal } from "./TimeoutWarningModal";
import { addEventListeners, removeEventListeners } from "./util/eventListenerUtil";

import { isDevTeam, Session, sessionConfig, loadSessionPolicy, handleLogout } from "src/utils/sessionUtils";
import { debugLog } from "src/utils/debugUtils";

export const TimeoutLogic = (props) => {
	const { trackEvent } = useTracking();

	const lod = useSelector((s) => s.configs.lod);
	const showTimeoutCounterFlag = useSelector((s) => s.configs.showTimeoutCounter);
	const showTimeoutShortWindowFlag = useSelector((s) => s.configs.showTimeoutShortWindow);

	const [isWarningModalOpen, setWarningModalOpen] = useState(false);
	const [timeouts, setTimeouts] = useState(() => sessionConfig());
	const lastKeepAliveAtRef = useRef(0);
	// Counter visibility and short-window behavior are independent toggles.
	const timeoutCounterMode = isDevTeam() && !!showTimeoutCounterFlag;
	const timeoutShortWindowMode = isDevTeam() && !!showTimeoutShortWindowFlag;
	const warningTime = timeoutShortWindowMode ? 30000 : props.warningTime || timeouts.warningTime;
	const redirectTime = timeoutShortWindowMode ? 15000 : props.redirectTime || timeouts.redirectTime;

	// Mutable debug counter (no re-render) used only for timeout diagnostics logs.
	const debugCounterRef = useRef(0);

	useEffect(() => {
		let mounted = true;
		// Bootstrap timeout values from backend policy so frontend timers match server session settings.
		loadSessionPolicy().finally(() => {
			if (mounted) {
				setTimeouts(sessionConfig());
			}
		});
		return () => {
			mounted = false;
		};
	}, []);

	useEffect(() => {
		// Debug-only counter for timeout diagnostics; increments every second.
		const id = setInterval(() => {
			debugCounterRef.current += 1;
			if (timeoutCounterMode) {
				console.log("Counter:", debugCounterRef.current);
				console.log({ lod, warningTime, redirectTime, timeoutShortWindowMode });
			}
		}, 1000);

		return () => {
			clearInterval(id);
		};
	}, [lod, timeoutCounterMode, timeoutShortWindowMode, warningTime, redirectTime]);

	useEffect(() => {
		// Timeout #1: open warning modal after idle warning window.
		const createTimeout1 = () =>
			setTimeout(() => {
				debugLog(3, "[TimeoutLogic][STATE] warningModalOpened", {
					sessionConfig: sessionConfig(),
				});
				setWarningModalOpen(true);
			}, warningTime);

		// Timeout #2: after warning modal grace window, force logout.
		const createTimeout2 = () =>
			setTimeout(() => {
				// Warning grace window elapsed: force logout to keep client and server auth state aligned.
				debugLog(2, "[TimeoutLogic][FLOW] sessionTimeoutRedirect", {
					sessionConfig: sessionConfig(),
				});

				const currentUser = Session()?.currUser;
				trackEvent({
					page: " - ",
					event: "sessionTimeout",
					props: {
						email: currentUser?.email || null,
						userId: currentUser?.id ?? null,
						statusId: currentUser?.status_id ?? null,
					},
				});
				handleLogout();
			}, redirectTime);

		const listener = () => {
			// Any qualifying user activity extends the warning timer chain.
			if (!isWarningModalOpen) {
				clearTimeout(timeout);
				timeout = createTimeout1();
				// Reset debug counter whenever user activity extends the client-side timeout window.
				debugCounterRef.current = 0;
				// Throttle keepalive pings so active users extend server session without chatty traffic.
				const now = Date.now();
				const keepAliveIntervalMs = 60 * 1000;
				// Keepalive is intentionally throttled; timer extension still happens immediately above.
				if (now - lastKeepAliveAtRef.current >= keepAliveIntervalMs) {
					lastKeepAliveAtRef.current = now;
					API.get("/session-policy")
						.then(() => {
							// Reset debug counter only when keepalive succeeds and session is extended.
							debugCounterRef.current = 0;
						})
						.catch(() => {});
				}
			}
		};

		// Keep one active timer chain and one listener; rebuild when warning state or policy windows change.
		let timeout = isWarningModalOpen ? createTimeout2() : createTimeout1();
		addEventListeners(listener);

		// Prevent duplicate listeners/timeouts across re-renders and on unmount.
		return () => {
			removeEventListeners(listener);
			clearTimeout(timeout);
		};

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isWarningModalOpen, warningTime, redirectTime]);

	const requestCloseHandler = () => {
		// Closing warning modal means user stays active; restart cycle from zero.
		setWarningModalOpen(false);
		debugCounterRef.current = 0;
	};

	return (
		<div>
			{isWarningModalOpen && (
				<TimeoutWarningModal
					isOpen={isWarningModalOpen}
					onRequestClose={requestCloseHandler}
				/>
			)}
		</div>
	);
};
